import { Popover } from "@base-ui/react/popover"
import React from "react"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { PopoverArrow } from "shared/ui/popover/ui/PopoverArrow"

import "./styles/_info-tip.scss"

export interface InfoTipProps {
  content: React.ReactNode
  /** If not provided, defaults to an info icon */
  label: string
  children?: React.ReactNode
  /** Icon to display when no children are provided */
  icon?: IconName
  iconSize?: IconSize
  /** Visual variant that applies modifier classes */
  variant?: "warning" | "default"
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Whether to open on hover (default: true) */
  openOnHover?: boolean
  side?: "top" | "bottom" | "left" | "right"
  sideOffset?: number
  className?: string
  popupClassName?: string
  "data-testid"?: string

  tabIndex?: number
  "aria-hidden"?: boolean
}

export const InfoTip = React.memo(
  ({
    content,
    children,
    label,
    icon = IconName.INFO,
    iconSize = IconSize.SMALL,
    variant = "default",
    defaultOpen = false,
    open,
    onOpenChange,
    openOnHover = true,
    side = "top",
    sideOffset = 8,
    className,
    popupClassName,
    tabIndex = 0,
    "aria-hidden": ariaHidden,
    ...rest
  }: InfoTipProps) => {
    const defaultTrigger = (
      <Icon name={icon} size={iconSize} className="info-tip__default-icon" />
    )

    const variantClassName = variant ? `info-tip__popup--${variant}` : undefined

    return (
      <Popover.Root
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={onOpenChange}
      >
        <Popover.Trigger
          {...rest}
          aria-label={label}
          className={classNames(
            "btn btn-secondary btn--borderless info-tip__trigger",
            className
          )}
          openOnHover={openOnHover}
          tabIndex={tabIndex}
          aria-hidden={ariaHidden}
        >
          {children || defaultTrigger}
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Positioner
            className="info-tip__positioner"
            side={side}
            sideOffset={sideOffset}
            positionMethod="fixed"
          >
            <Popover.Popup
              className={classNames(
                "info-tip__popup",
                variantClassName,
                popupClassName
              )}
              // Base UI renders the popup with role="dialog", which requires an
              // accessible name. Reuse the trigger's label — it already
              // describes what the popup is about.
              aria-label={label}
            >
              <Popover.Arrow className="info-tip__arrow">
                <PopoverArrow
                  fillClassName="info-tip__arrow-fill"
                  outerStrokeClassName="info-tip__arrow-outer-stroke"
                  innerStrokeClassName="info-tip__arrow-inner-stroke"
                />
              </Popover.Arrow>
              <div className="info-tip__content">{content}</div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    )
  }
)
